import { put, takeEvery } from 'redux-saga/effects';
import {
  AssessmentAction,
  AssessmentActionTypes,
  AssessmentActions,
} from '../actions/AssessmentActions';
import {
  IAssessessments,
  IAssessessmentsPayload,
  IAssessmentOptions,
  IAssessmentQuestions,
  IAssessmentQuestionsPayload,
  ICloudAssessment,
  IDeleteAssessmentPayload,
  IDownloadAssessmentPayload,
  IDownloadCloudAssessmentPayload,
  IDownloadCloudAssessmentResponse,
  IFetchCloudAssessmentsPayload,
  IGenerateCloudAssessmentPayload,
  ISubmitAssessmentPayload,
} from '../../models/AssessmentModels';
import assessmentService from '../../services/AssessmentService/AssessmentService';
import { ToastActions } from '../actions/ToastActions';
import { NotificationTypes } from '../../../components/notifications/Toast/ToastConstants';
import { CloudServiceProvider } from '../../../constants/AppConstants';

function* genericErrorWorker(
  action: AssessmentAction,
  workerMethod: (action: AssessmentAction) => any,
  failureMethod: (data: Error) => {
    type: AssessmentActionTypes;
    data: Error;
  },
) {
  try {
    yield workerMethod(action);
  } catch (error) {
    if (error instanceof Error) {
      yield put(
        ToastActions.showToast({
          description: error.message,
          type: NotificationTypes.ERROR,
        }),
      );
      yield put(failureMethod(error));
    }
  }
}

export function* fetchAssessmentOptionsWorker() {
  const { csp_list, capability_list } = yield assessmentService.fetchAssessmentOptions();
  const response: IAssessmentOptions = {
    cspList: csp_list.map((csp: string) => csp.toLowerCase() as CloudServiceProvider),
    capabilitiesList: capability_list,
  };
  yield put(AssessmentActions.getAssessmentOptionsSuccess(response));
}

export function* fetchAssessmentQuestionsWorker(action: AssessmentAction) {
  const { capabilities, cloud_service_provider } = yield assessmentService.fetchAssessmentQuestions(
    action.data as IAssessmentQuestionsPayload,
  );
  const response: IAssessmentQuestions = {
    cloudServiceProviders: cloud_service_provider.map(
      (csp: string) => csp.toLowerCase() as CloudServiceProvider,
    ),
    capabilities: capabilities,
  };
  yield put(AssessmentActions.getAssessmentQuestionsSuccess(response));
}

export function* fetchAssessmentsWorker(action: AssessmentAction) {
  const assessmentsList: Array<any> = yield assessmentService.fetchAssessments(
    action.data as IAssessessmentsPayload,
  );
  const response: Array<IAssessessments> = assessmentsList.map(({ csp, ...rest }) => {
    const assessment: IAssessessments = {
      ...rest,
      cloudServiceProviders: csp.map(
        (providerf: string) => providerf.toLowerCase() as CloudServiceProvider,
      ),
    };
    return assessment;
  });
  yield put(AssessmentActions.getAssessmentsSuccess(response));
}

export function* downloadAssessmentWorker(action: AssessmentAction) {
  const response: string = yield assessmentService.downloadAssessment(
    action.data as IDownloadAssessmentPayload,
  );
  const content = (JSON.parse(response).data as Array<{ [key: string]: string }>).slice(1);
  yield put(
    AssessmentActions.downloadAssessmentSuccess({
      content: content,
      assessmentName: (action.data as IDownloadAssessmentPayload).assessmentName,
    }),
  );
}

export function* deleteAssessmentWorker(action: AssessmentAction) {
  const response: string = yield assessmentService.deleteAssessment(
    action.data as IDeleteAssessmentPayload,
  );
  yield put(AssessmentActions.deleteAssessmentSuccess());
  yield put(
    ToastActions.showToast({
      description: response,
      type: NotificationTypes.SUCCESS,
    }),
  );
  yield put(AssessmentActions.getAssessmentsStart(action.data as IDeleteAssessmentPayload));
}

export function* submitAssessmentWorker(action: AssessmentAction) {
  yield assessmentService.submitAssessment(action.data as ISubmitAssessmentPayload);
  yield put(AssessmentActions.submitAssessmentSuccess());
  yield put(
    ToastActions.showToast({
      description: `Submitted ${
        (action.data as ISubmitAssessmentPayload).assessmentName
      } successfulley`,
      type: NotificationTypes.SUCCESS,
    }),
  );
  yield put(
    AssessmentActions.getAssessmentsStart({
      userId: (action.data as ISubmitAssessmentPayload).userId,
      projectId: (action.data as ISubmitAssessmentPayload).projectId,
    }),
  );
}

export function* generateCloudAssessmentWorker(action: AssessmentAction) {
  const response: ICloudAssessment = yield assessmentService.generateAssessment(
    action.data as IGenerateCloudAssessmentPayload,
  );
  yield put(AssessmentActions.generateCloudAssessmentSuccess(response));
  yield put(
    ToastActions.showToast({
      description: `Generated ${response.assessment_name} successfulley`,
      type: NotificationTypes.SUCCESS,
    }),
  );
  const { userData } = action.data as IGenerateCloudAssessmentPayload;
  yield put(
    AssessmentActions.fetchCloudAssessmentsStart({
      accountId: JSON.parse(userData).account_id,
    }),
  );
}

export function* fetchCloudAssessmentsWorker(action: AssessmentAction) {
  const response: Array<ICloudAssessment> = yield assessmentService.fetchCloudAssessments(
    action.data as IFetchCloudAssessmentsPayload,
  );
  const updatedResponse = response.map((assessment: ICloudAssessment) => ({
    ...assessment,
    cloud_providers: assessment.cloud_providers.map(
      (csp: string) => csp.toLowerCase() as CloudServiceProvider,
    ),
  }));
  yield put(AssessmentActions.fetchCloudAssessmentsSuccess(updatedResponse));
}

export function* fetchCloudAssessmentByIdWorker(action: AssessmentAction) {
  const { capabilities, cloud_service_provider } = yield assessmentService.fetchAssessmentQuestions(
    action.data as IAssessmentQuestionsPayload,
  );
  const response: IAssessmentQuestions = {
    cloudServiceProviders: cloud_service_provider.map(
      (csp: string) => csp.toLowerCase() as CloudServiceProvider,
    ),
    capabilities: capabilities,
  };
  yield put(AssessmentActions.getAssessmentQuestionsSuccess(response));
}

export function* downloadCloudAssessmentReportWorker(action: AssessmentAction) {
  const response: IDownloadCloudAssessmentResponse =
    yield assessmentService.downloadCloudAssessmentReport(
      action.data as IDownloadCloudAssessmentPayload,
    );
  yield put(
    ToastActions.showToast({
      description: `Downloading report...`,
      type: NotificationTypes.SUCCESS,
    }),
  );
  yield put(AssessmentActions.downloadCloudAssessmentReportSuccess(response));
}

export function* hardReloadWorker() {
  yield put(AssessmentActions.handleHardReload());
}

export function* assessmentWatcher() {
  yield takeEvery(AssessmentActionTypes.FETCH_ASSESS_OPTIONS_START, (action: AssessmentAction) =>
    genericErrorWorker(
      action,
      fetchAssessmentOptionsWorker,
      AssessmentActions.getAssessmentOptionsFailure,
    ),
  );
  yield takeEvery(AssessmentActionTypes.FETCH_ASSESS_QUESTIONS_START, (action: AssessmentAction) =>
    genericErrorWorker(
      action,
      fetchAssessmentQuestionsWorker,
      AssessmentActions.getAssessmentQuestionsFailure,
    ),
  );
  yield takeEvery(AssessmentActionTypes.FETCH_ASSESSMENTS_START, (action: AssessmentAction) =>
    genericErrorWorker(action, fetchAssessmentsWorker, AssessmentActions.getAssessmentsFailure),
  );
  yield takeEvery(AssessmentActionTypes.DOWNLOAD_ASSESSMENT_START, (action: AssessmentAction) =>
    genericErrorWorker(
      action,
      downloadAssessmentWorker,
      AssessmentActions.downloadAssessmentFailure,
    ),
  );
  yield takeEvery(AssessmentActionTypes.SUBMIT_ASSESSMENT_START, (action: AssessmentAction) =>
    genericErrorWorker(action, submitAssessmentWorker, AssessmentActions.submitAssessmentFailure),
  );
  yield takeEvery(AssessmentActionTypes.DELETE_ASSESSMENT_START, (action: AssessmentAction) =>
    genericErrorWorker(action, deleteAssessmentWorker, AssessmentActions.deleteAssessmentFailure),
  );
  yield takeEvery(
    AssessmentActionTypes.GENERATE_CLOUD_ASSESSMENT_START,
    (action: AssessmentAction) =>
      genericErrorWorker(
        action,
        generateCloudAssessmentWorker,
        AssessmentActions.generateCloudAssessmentFailure,
      ),
  );
  yield takeEvery(AssessmentActionTypes.FETCH_CLOUD_ASSESSMENTS_START, (action: AssessmentAction) =>
    genericErrorWorker(
      action,
      fetchCloudAssessmentsWorker,
      AssessmentActions.fetchCloudAssessmentByIdFailure,
    ),
  );
  yield takeEvery(
    AssessmentActionTypes.FETCH_CLOUD_ASSESSMENT_BY_ID_START,
    (action: AssessmentAction) =>
      genericErrorWorker(
        action,
        fetchCloudAssessmentByIdWorker,
        AssessmentActions.generateCloudAssessmentFailure,
      ),
  );
  yield takeEvery(
    AssessmentActionTypes.DOWNLOAD_CLOUD_ASSESSMENT_REPORT_START,
    (action: AssessmentAction) =>
      genericErrorWorker(
        action,
        downloadCloudAssessmentReportWorker,
        AssessmentActions.generateCloudAssessmentFailure,
      ),
  );
  yield takeEvery('persist/REHYDRATE', hardReloadWorker);
}
